import React, { useState, useEffect, useCallback } from "react";
import { Spinner, Pagination, Collapse } from "react-bootstrap";
import { FaArrowLeft, FaHistory, FaChevronDown, FaChevronRight, FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import apiClient from "../../utils/apiClient";
import { getCurrentUser } from "../../utils/auth";
import PracticeStatsBar from "./PracticeStatsBar";
import "./practice.scss";

const PER_PAGE = 20;

const statusLabel = (s) =>
  s === "solved" ? "Solved" : s === "partially_solved" ? "Partial" : "Attempted";

const formatDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" }) +
    " " +
    dt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" });
};

const PracticeHistoryPage = () => {
  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  const studentId = currentUser?.studentId;

  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [expandedId, setExpandedId] = useState(null);

  const fetchHistory = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/practice/history/${studentId}`, {
        params: { page, limit: PER_PAGE },
      });
      setAttempts(data.attempts || []);
      setTotalPages(data.totalPages || 1);
    } catch {
      setAttempts([]);
    }
    setLoading(false);
  }, [studentId, page]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  return (
    <div className="container-fluid py-4 px-3 px-md-4">
      {/* Header */}
      <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
        <button
          className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
          onClick={() => navigate("/s/practice")}
        >
          <FaArrowLeft size={12} /> Back
        </button>
        <h4 className="fw-bold mb-0">
          <FaHistory className="me-2 text-primary" />
          Practice History
        </h4>
      </div>

      {/* Stats */}
      <PracticeStatsBar studentId={studentId} />

      {/* Table */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : attempts.length === 0 ? (
        <div className="practice-empty">
          <FaHistory />
          <span>No attempts yet — start practicing!</span>
        </div>
      ) : (
        <>
          <div className="practice-history-table">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th style={{ width: 32 }}></th>
                  <th>Question</th>
                  <th>Difficulty</th>
                  <th>Status</th>
                  <th>Passed</th>
                  <th>Language</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {attempts.map((a) => (
                  <React.Fragment key={a._id}>
                    <tr
                      className="practice-history-row"
                      onClick={() => toggleExpand(a._id)}
                      style={{ cursor: "pointer" }}
                    >
                      <td>
                        {expandedId === a._id ? (
                          <FaChevronDown size={11} className="text-muted" />
                        ) : (
                          <FaChevronRight size={11} className="text-muted" />
                        )}
                      </td>
                      <td className="fw-medium">{a.question?.title || "—"}</td>
                      <td>
                        <span className={`practice-diff-badge ${(a.question?.difficulty || "").toLowerCase()}`}>
                          {a.question?.difficulty || "—"}
                        </span>
                      </td>
                      <td>
                        <span className={`practice-status-badge ${a.status}`}>
                          {statusLabel(a.status)}
                        </span>
                      </td>
                      <td>
                        {a.passedCount}/{a.totalCount}
                      </td>
                      <td>{a.language || "—"}</td>
                      <td style={{ fontSize: "0.8rem" }}>{formatDate(a.createdAt)}</td>
                    </tr>
                    {/* Expanded row */}
                    <tr>
                      <td colSpan={7} className="p-0 border-0">
                        <Collapse in={expandedId === a._id}>
                          <div className="practice-history-detail">
                            {/* Code */}
                            <div className="mb-3">
                              <strong className="d-block mb-1" style={{ fontSize: "0.8rem" }}>
                                Code ({a.language})
                              </strong>
                              <pre className="practice-history-code">{a.code}</pre>
                            </div>

                            {/* Test case results */}
                            {a.results && a.results.length > 0 && (
                              <div>
                                <strong className="d-block mb-1" style={{ fontSize: "0.8rem" }}>
                                  Test Case Results
                                </strong>
                                <div className="practice-history-tc-list">
                                  {a.results.filter(r => !r.isHidden).map((r, idx) => (
                                    <div key={idx} className={`practice-history-tc ${r.passed ? "passed" : "failed"}`}>
                                      <span className="me-2">
                                        {r.passed ? (
                                          <FaCheckCircle className="text-success" />
                                        ) : (
                                          <FaTimesCircle className="text-danger" />
                                        )}
                                      </span>
                                      <span className="text-muted me-2" style={{ fontSize: "0.75rem" }}>
                                        TC {idx + 1}
                                      </span>
                                      <span style={{ fontSize: "0.8rem" }}>
                                        Input: <code>{r.input}</code> &nbsp;|&nbsp; Expected:{" "}
                                        <code>{r.expected}</code> &nbsp;|&nbsp; Got:{" "}
                                        <code>{r.output || "—"}</code>
                                      </span>
                                    </div>
                                  ))}
                                  {(() => {
                                    const hidden = (a.results || []).filter(r => r.isHidden);
                                    if (!hidden.length) return null;
                                    const hp = hidden.filter(r => r.passed).length;
                                    return (
                                      <div className={`practice-history-tc ${hp === hidden.length ? "passed" : "failed"}`}>
                                        <span className="me-2">
                                          {hp === hidden.length ? <FaCheckCircle className="text-success" /> : <FaTimesCircle className="text-danger" />}
                                        </span>
                                        <span style={{ fontSize: "0.8rem" }}>
                                          {hp}/{hidden.length} hidden test case{hidden.length !== 1 ? "s" : ""} passed
                                        </span>
                                      </div>
                                    );
                                  })()}
                                </div>
                              </div>
                            )}
                          </div>
                        </Collapse>
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-3">
              <Pagination size="sm">
                <Pagination.Prev disabled={page <= 1} onClick={() => setPage((p) => p - 1)} />
                {[...Array(totalPages)].map((_, i) => (
                  <Pagination.Item key={i + 1} active={page === i + 1} onClick={() => setPage(i + 1)}>
                    {i + 1}
                  </Pagination.Item>
                ))}
                <Pagination.Next disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} />
              </Pagination>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PracticeHistoryPage;
